import { MouseEventHandler, PropsWithChildren, useState } from 'react'
import { Icon } from '@/components/atoms'
import classnames from 'classnames'
import { ReactNode } from '@tanstack/react-router'
import React from 'react'
import { NestedCheckboxGroupOptionType } from '@/components'

type TreeNodeProps<T = unknown> = PropsWithChildren & {
    label: T
    isRoot?: boolean
    renderLabel?: (label: T, isOpen: boolean) => ReactNode
}

const defaultRenderLabel = (label: unknown): ReactNode => {
    if (typeof label === 'string') {
        return label
    }

    return null
}

export const TreeNode = <T,>(props: TreeNodeProps<T>): React.ReactElement => {
    const { children, label, isRoot } = props
    const renderLabel = props.renderLabel ?? defaultRenderLabel
    const [isOpen, setIsOpen] = useState(false)
    const hasChildren =
        !!children &&
        React.isValidElement<{ options?: NestedCheckboxGroupOptionType[] }>(
            children
        ) &&
        !!children.props.options &&
        children.props.options.length > 0

    const childrenWrapperClassname = classnames(
        'relative grid grid-rows-[0fr] transition-all pointer-events-none',
        {
            '[&[class]]:grid-rows-[1fr]': isOpen,
        }
    )

    const wrapperClassname = classnames({
        'group/node': !isRoot,
    })

    return (
        <div className={wrapperClassname}>
            <div className="relative z-20 pl-2">
                {!isRoot && (
                    <div className="pointer-events-none absolute -left-2 bottom-1/2 -ml-[1px] h-5 w-[17px] translate-y-[0.5px] rounded-bl-lg border-b border-l border-neutral-300"></div>
                )}

                <div className="flex items-center gap-1">
                    {hasChildren && (
                        <TreeNodeToggle
                            isOpen={isOpen}
                            isRoot={isRoot}
                            onClick={() => {
                                setIsOpen((isOpen) => !isOpen)
                            }}
                        />
                    )}
                    <div className="grow">{renderLabel(label, isOpen)}</div>
                </div>
            </div>
            {hasChildren && (
                <div className={childrenWrapperClassname}>
                    <div
                        className="pointer-events-auto ml-2 overflow-hidden pl-5"
                        data-testid={'children-wrapper'}
                    >
                        <div className="pt-2">{children}</div>
                    </div>
                    <div className="absolute -top-5 bottom-8 left-3 ml-[7px] border-l border-neutral-300"></div>
                    <div className="absolute -left-2 -top-7 bottom-8 z-10 -ml-[1px] hidden border-l border-white group-last/node:block"></div>
                </div>
            )}
        </div>
    )
}

type TreeNodeToggleProps = {
    isOpen: boolean
    onClick: MouseEventHandler
    isRoot?: boolean
}

const TreeNodeToggle: React.FC<TreeNodeToggleProps> = (props) => {
    const { isOpen, onClick, isRoot } = props

    return (
        <div
            className="relative flex cursor-pointer"
            onClick={onClick}
            data-testid={'node-toggle'}
        >
            {/* creates white disc behind icon to hide borders */}
            <div className="absolute inset-0 m-auto h-[16px] w-[16px] rounded-full bg-white" />
            {/* small border to connect border to icon */}
            {!isRoot && (
                <div className="absolute left-0 top-1/2 w-[2px] -translate-y-1/2 border-t border-neutral-300" />
            )}
            {/* the actual icon */}
            <Icon
                className="relative text-icon-small text-azure"
                name={!isOpen ? 'expand_circle_down' : 'expand_circle_up'}
            />
        </div>
    )
}
